import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { environment } from '@env/environment';
import { NgbActiveModal, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { InfoNoticeComponent } from '@shared/components/atoms/info-notice/info-notice.component';
import { DynamicFormComponent } from '@shared/components/dynamic-form/components/dynamic-form/dynamic-form.component';
import { DynamicField } from '@shared/components/dynamic-form/models/field';
import { finalize, Observable } from 'rxjs';

export type EventQueryFormDialogResult =
  | { action: 'submitted'; value: Record<string, unknown> }
  | { action: 'skip' };

export type EventQueryFormDialogMode = 'submit' | 'update';

@Component({
  selector: 'app-event-query-form-dialog',
  imports: [
    CommonModule,
    NgbModalModule,
    DynamicFormComponent,
    TranslateModule,
    InfoNoticeComponent,
  ],
  templateUrl: './event-query-form-dialog.component.html',
})
export class EventQueryFormDialogComponent implements OnInit {
  @Input() fields: DynamicField[] = [];
  @Input() onBehalfName = '';
  @Input() mode: EventQueryFormDialogMode = 'submit';
  @Input() initialFormValue: Record<string, unknown> = {};
  @Input() submitHandler: (
    value: Record<string, unknown>
  ) => Observable<unknown>;

  @ViewChild('queryForm') dynamicFormComponent!: DynamicFormComponent;

  readonly activeModal = inject(NgbActiveModal);

  formValue: Record<string, unknown> = {};
  formValid = false;
  submitting = false;

  get updateMode(): boolean {
    return this.mode === 'update';
  }

  get submitLabelKey(): string {
    return this.updateMode
      ? 'EventQuery.Dialog.Update'
      : 'EventQuery.Dialog.Submit';
  }

  get canSkip(): boolean {
    return (
      !this.updateMode &&
      !this.fields.some(
        field => (field.settings as { required?: boolean })?.required
      )
    );
  }

  get canCancel(): boolean {
    const onProcessTokenPage = globalThis.location?.pathname?.includes(
      environment.jipUrl.processToken
    );

    return !onProcessTokenPage;
  }

  ngOnInit(): void {
    this.formValue = { ...this.initialFormValue };
  }

  onValueChanged(value: unknown): void {
    this.formValue = (value as Record<string, unknown>) ?? {};
  }

  submit(): void {
    this.dynamicFormComponent?.formSubmitted.set(true);

    if (!this.formValid || this.submitting) {
      return;
    }

    if (!this.submitHandler) {
      this.activeModal.close({ action: 'submitted', value: this.formValue });
      return;
    }

    this.submitting = true;

    this.submitHandler(this.formValue)
      .pipe(finalize(() => (this.submitting = false)))
      .subscribe({
        next: () =>
          this.activeModal.close({
            action: 'submitted',
            value: this.formValue,
          }),
      });
  }

  skip(): void {
    this.activeModal.close({ action: 'skip' });
  }

  cancel(): void {
    this.activeModal.dismiss();
  }
}
